import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

// Third Party Libraries
import DatePicker from '~/components/General/OlcDatePicker';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import {
  CLEAR_ADMIN_USERS_FILTERS,
  SET_ADMIN_USERS_FILTERS,
} from '~/redux/actions/action-types.js';
import { failure } from '~/redux/actions/snackbar-actions';

// Utils
import { DATE_REGEX, DEFAULT_PAGINATION, USER_ROLES } from '~/utils/constants.js';
import { MESSAGES } from '~/utils/message';
import {
  dateProtector,
  handleRawChangeForDatePicker,
  formatDateOnly,
  parseLocalDateOnly,
} from '~/utils/date-format.js';

// Components
import MultiSelect from '~/components/General/MultiSelectNew';

// Mui components
import Typography from '@mui/material/Typography';
import { Box, Checkbox, FormControlLabel, Grid, InputLabel } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DoneIcon from '@mui/icons-material/Done';

// Styles
import './styles.scss';

const AdvanceFilters = forwardRef((props, ref) => {
  const { handleCloseFilter, open, updatePagination } = props;
  const [selectedPlan, setSelectedPlan] = useState([]);
  const [selectedRole, setSelectedRole] = useState([]);
  const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState([]);
  const [signUpDate, setSignUpDate] = useState([null, null]);
  const [showSystemAdmins, setShowSystemAdmins] = useState(false);
  const [startDate, endDate] = signUpDate;

  const dispatch = useDispatch();

  const companies = useSelector((state) => state.adminUsersReducers.filterslabels.companies);

  const plans = useSelector((state) => state.adminUsersReducers.filterslabels.plans);

  const perPage = useSelector(
    (state) => state.adminUsersReducers.perPage || DEFAULT_PAGINATION.pageSize
  );

  const filters = useSelector((state) => state.adminUsersReducers.filters);

  const onClose = () => {
    clearFilters(true);
  };

  const clearFilters = (persist = false) => {
    setSelectedPlan([]);
    setSelectedRole([]);
    setSelectedCompany([]);
    setSignUpDate([null, null]);
    setShowSystemAdmins(false);
    !persist ? dispatch({ type: CLEAR_ADMIN_USERS_FILTERS }) : undefined;
    handleCloseFilter();
  };

  const applyFilters = () => {
    const dateRange =
      startDate && endDate
        ? {
            startDate: formatDateOnly(startDate),
            endDate: formatDateOnly(endDate),
          }
        : null;

    if (startDate && endDate === null) {
      dispatch(failure(MESSAGES.CONTACTS.END_DATE_REQUIRED));
      return;
    }

    let payload = {
      applyFilters: true,
      filters: {
        plan: plans.filter((plan) => selectedPlan.includes(plan.title)).map((plan) => plan.value),
        role: USER_ROLES.filter((role) => selectedRole.includes(role.title)).map(
          (role) => role.value
        ),
        companyName: selectedCompany,
        signUpDate: dateRange,
      },
    };
    if (showSystemAdmins) {
      payload.filters.isSystemAdmin = true;
    }

    const filtersWithValues = Object.keys(payload.filters).filter((key) => {
      const value = payload.filters[key];
      return Array.isArray(value) ? value.length > 0 : value !== null;
    });

    payload.filterSize = filtersWithValues.length;

    updatePagination({
      pageSize: perPage,
      page: 1,
    });

    if (filtersWithValues.length <= 0) {
      dispatch(failure(MESSAGES.ORDERS.FILTERS_REQUIRED));
      return;
    }

    dispatch({ type: SET_ADMIN_USERS_FILTERS, payload: payload });
    onClose();
  };

  useImperativeHandle(ref, () => ({
    clearFilters() {
      clearFilters();
    },
  }));

  useEffect(() => {
    if (Object.values(filters).length) {
      setSelectedPlan(
        plans.filter((plan) => filters.plan.includes(plan.value)).map((plan) => plan.title)
      );
      setSelectedRole(
        USER_ROLES.filter((role) => filters.role.includes(role.value)).map((role) => role.title)
      );
      setSelectedCompany(filters.companyName);
      filters.signUpDate
        ? setSignUpDate([
            parseLocalDateOnly(filters?.signUpDate?.startDate),
            parseLocalDateOnly(filters?.signUpDate?.endDate),
          ])
        : undefined;
      setShowSystemAdmins(filters.isSystemAdmin);
    }
  }, [open]);

  return (
    <OlcFormModal
      open={open}
      onClose={onClose}
      title={MESSAGES.ADMIN_DASHBOARD.USERS.ADVANCE_FILTERS.HEADING}
      className="userfilterModalWrapper olcAdminFilterModal"
      contentClassName="filterModalContent"
      footer={
        <>
          <OlcButton variant="secondary" onClick={() => clearFilters()}>
            {MESSAGES.ADMIN_DASHBOARD.USERS.ADVANCE_FILTERS.CANCEL_BUTTON}
          </OlcButton>
          <OlcButton variant="primary" onClick={applyFilters}>
            {MESSAGES.ADMIN_DASHBOARD.USERS.ADVANCE_FILTERS.SUBMIT_BUTTON}
          </OlcButton>
        </>
      }
    >
      <Grid container className="adminUsersFilterGrid">
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="inputWrapper">
            <Typography>{MESSAGES.ADMIN_DASHBOARD.USERS.ADVANCE_FILTERS.PLAN_LABEL}</Typography>
            <MultiSelect
              options={plans}
              selectedValue={selectedPlan}
              setSelectedValue={setSelectedPlan}
              placeHolderText={MESSAGES.ADMIN_DASHBOARD.USERS.ADVANCE_FILTERS.PLAN_PLACEHOLDER}
              noOptionText={MESSAGES.ADMIN_DASHBOARD.USERS.ADVANCE_FILTERS.NO_PLAN}
              clearable={true}
            />
          </Box>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="mailedDate">
            <Box className="selectDate">
              <InputLabel className="dateLabel">
                {MESSAGES.ADMIN_DASHBOARD.USERS.ADVANCE_FILTERS.SIGN_UP_DATE_LABEL}
              </InputLabel>
              <KeyboardArrowDownIcon
                sx={{
                  color: isDatepickerOpen ? '#ED5C2F' : 'inherit',
                }}
              />
              <DatePicker
                selectsRange={true}
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => {
                  dateProtector(update);
                  setSignUpDate(update);
                }}
                minDate={null}
                onChangeRaw={(e) => {
                  if (e.target.value === '' || DATE_REGEX.test(e.target.value)) {
                    handleRawChangeForDatePicker(e, setSignUpDate);
                  } else {
                    e.preventDefault();
                  }
                }}
                onFocus={() => setIsDatepickerOpen(true)}
                onBlur={(e) => {
                  if (e.target.value == '') {
                    setSignUpDate([null, null]);
                  }
                  setIsDatepickerOpen(false);
                }}
                placeholderText={MESSAGES.ADMIN_DASHBOARD.USERS.ADVANCE_FILTERS.DATE_PLACEHOLDER}
                isClearable
              />
            </Box>
          </Box>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="inputWrapper">
            <Typography>{MESSAGES.ADMIN_DASHBOARD.USERS.ADVANCE_FILTERS.ROLE_LABEL}</Typography>
            <MultiSelect
              options={USER_ROLES}
              selectedValue={selectedRole}
              setSelectedValue={setSelectedRole}
              placeHolderText={MESSAGES.ADMIN_DASHBOARD.USERS.ADVANCE_FILTERS.ROLE_PLACEHOLDER}
              noOptionText={MESSAGES.ADMIN_DASHBOARD.USERS.ADVANCE_FILTERS.NO_ROLE}
              clearable={true}
            />
          </Box>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="inputWrapper">
            <Typography>{MESSAGES.ADMIN_DASHBOARD.USERS.ADVANCE_FILTERS.COMPANY_LABEL}</Typography>
            <MultiSelect
              options={companies}
              selectedValue={selectedCompany}
              setSelectedValue={setSelectedCompany}
              placeHolderText={MESSAGES.ADMIN_DASHBOARD.USERS.ADVANCE_FILTERS.COMPANY_PLACEHOLDER}
              noOptionText={MESSAGES.ADMIN_DASHBOARD.USERS.ADVANCE_FILTERS.NO_COMPANY}
              clearable={true}
            />
          </Box>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="adminFilterCheckWrapper">
            <FormControlLabel
              className="adminFilterCheck"
              control={
                <Checkbox
                  className={`customCheckbox checked`}
                  icon={<Box className="notChecked"></Box>}
                  checked={showSystemAdmins}
                  style={{ pointerEvents: 'auto' }}
                  disableTypography={true}
                  onClick={() => {
                    setShowSystemAdmins((prev) => !prev);
                  }}
                  checkedIcon={<DoneIcon />}
                  disableRipple={true}
                />
              }
              label={MESSAGES.ADMIN_DASHBOARD.USERS.ADVANCE_FILTERS.SYSTEM_ADMIN_LABEL}
            />
          </Box>
        </Grid>
      </Grid>
    </OlcFormModal>
  );
});

export default AdvanceFilters;
